import { useMemo } from 'react';
import { css } from '@emotion/react';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { DropdownField, DropdownFilterChipField, useForm } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../hooks';

const UNASSIGNED_OPTION = { label: 'Unassigned', value: 'unassigned' };

export const TableCustomFilters = ({ dataSourcesData }: { dataSourcesData: DataSource[] }) => {
  const { t } = useTranslation('scheduleGlobalSettings');
  const { selectedLocationIds, getLocationName } = useSchedulingLocationInfo();

  const locationOptions = useMemo(() => {
    const selectedLocationOptions = selectedLocationIds.map((locationId) => ({
      label: getLocationName(locationId),
      value: locationId,
    }));
    return [...selectedLocationOptions, UNASSIGNED_OPTION];
  }, [selectedLocationIds, getLocationName]);

  const sourceOptions = useMemo(
    () =>
      dataSourcesData.map((dataSource) => ({
        label: dataSource.sourceName,
        value: dataSource.sourceId,
      })),
    [dataSourcesData]
  );

  const { getFieldProps } = useForm({
    fields: {
      location: {
        type: 'dropdown',
        required: true,
        value: 'unassigned',
      },
      source: {
        type: 'checklist',
        required: true,
        value: [],
      },
    },
    fieldStateReducer: (state) => {
      // TODO: Handle list filtering when API supports it
      return state;
    },
  });

  return (
    <div css={containerStyles}>
      <DropdownField
        {...getFieldProps('location')}
        label={t('Location')}
        startAdornment={<Icon name='location-small' css={{ marginRight: theme.spacing(1) }} />}
      >
        {locationOptions.map(({ label, value }) => (
          <DropdownField.Option key={value} value={value}>
            {label}
          </DropdownField.Option>
        ))}
      </DropdownField>
      <DropdownFilterChipField
        {...getFieldProps('source')}
        icon='network-small'
        label={t('Source')}
        getCustomLabel={(items) => (items.length > 1 ? `${items.length} Sources` : items[0])}
        onApply={({ close }) => close()}
        showClearAll={false}
        showSelectAll={false}
        showSelectionBar={false}
        showSearchBar={false}
      >
        {sourceOptions.map(({ label, value = '' }) => (
          <DropdownFilterChipField.Option key={value} value={value}>
            {label}
          </DropdownFilterChipField.Option>
        ))}
      </DropdownFilterChipField>
    </div>
  );
};

const containerStyles = css({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
});
